import type React from 'react';
import { useCallback } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCookieConsent } from 'app/hooks/use-cookie-consent';
import Typography from 'app/ui/components/atoms/typography';
import Button from 'app/ui/components/atoms/button';
import { routes } from 'app/routes/constants';
import CookieIconSvg from 'app/assets/cookie-icon.svg';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: ${({ theme: { spacing } }) => spacing(4)};

  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        justify-content: flex-end;
        padding: ${spacing(6)};
      `,
    )}
  `}
`;

const ModalContainer = styled.div`
  background-color: ${({ theme: { colors } }) => colors.veryGray};
  border-radius: ${({ theme: { spacing } }) => spacing(3)};
  padding: ${({ theme: { spacing } }) => spacing(5)};
  max-width: 100%;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid ${({ theme: { colors } }) => colors.lightGray}20;

  ${({ theme: { media, spacing } }) => `
    ${media.medium(
      () => `
        max-width: 420px;
        border-radius: ${spacing(4)};
        padding: ${spacing(6)};
      `,
    )}
  `}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme: { spacing } }) => spacing(4)};
`;

const CookieIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  margin-right: ${({ theme: { spacing } }) => spacing(3)};
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
  }
`;

const Content = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => spacing(6)};
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(3)};
`;

const LinkText = styled.span`
  color: ${({ theme: { colors } }) => colors.knop};
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: ${({ theme: { colors } }) => colors.white};
  }
`;

interface CookieConsentModalProps {
  onAcceptAll: () => void;
  onDeclineNonEssential: () => void;
}

const CookieConsentModalContent: React.FC<CookieConsentModalProps> = ({ onAcceptAll, onDeclineNonEssential }) => {
  const intl = useIntl();

  const handleTermsClick = useCallback(() => {
    window.open(routes.TERMS_CONDITIONS, '_blank');
  }, []);

  const handlePrivacyClick = useCallback(() => {
    window.open('https://fiit.tv/privacy', '_blank');
  }, []);

  return (
    <ModalContainer>
      <Header>
        <CookieIconWrapper>
          <img src={CookieIconSvg} alt="Cookie Icon" />
        </CookieIconWrapper>
        <Typography variant="h4" color="white">
          <FormattedMessage id="cookieConsent.header" />
        </Typography>
      </Header>

      <Content>
        <Typography variant="caption" color="lightGray">
          <FormattedMessage
            id="cookieConsent.content"
            values={{
              termsLink: (
                <LinkText onClick={handleTermsClick}>
                  {intl.formatMessage({ id: 'cookieConsent.links.termsConditions' })}
                </LinkText>
              ),
              privacyLink: (
                <LinkText onClick={handlePrivacyClick}>
                  {intl.formatMessage({ id: 'cookieConsent.links.privacyPolicy' })}
                </LinkText>
              ),
            }}
          />
        </Typography>
      </Content>

      <ButtonGroup>
        <Button
          type="primary"
          onClick={onAcceptAll}
          label={intl.formatMessage({ id: 'cookieConsent.buttons.allowAll' })}
          fullWidthMob={true}
        />
        <Button
          type="secondary"
          onClick={onDeclineNonEssential}
          label={intl.formatMessage({ id: 'cookieConsent.buttons.necessaryOnly' })}
          fullWidthMob={true}
        />
      </ButtonGroup>
    </ModalContainer>
  );
};

export const CookieConsentModal: React.FC = () => {
  const { hasConsentBeenSet, acceptAll, declineNonEssential } = useCookieConsent();

  const handleAcceptAll = useCallback(() => {
    acceptAll();
  }, [acceptAll]);

  const handleDeclineNonEssential = useCallback(() => {
    declineNonEssential();
  }, [declineNonEssential]);

  // Don't show cookie consent modal if app is running in an iframe
  const isInIframe = window.self !== window.top;

  if (hasConsentBeenSet || isInIframe) {
    return null;
  }

  return (
    <Overlay data-testid="cookie-consent-modal">
      <CookieConsentModalContent onAcceptAll={handleAcceptAll} onDeclineNonEssential={handleDeclineNonEssential} />
    </Overlay>
  );
};

export default CookieConsentModal;
